import { Modal, Typography, Card, Box, Grid, IconButton, Icon, Button, Snackbar, Alert, FormControl, MenuItem, Select, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CancelIcon from '../../../icons/cancelIcon.svg';
import InviteResidentIcon from '../../../icons/InviteResidentIcon.svg';
import { InputField } from '../../../components/InputController';
import { ApplicantTypeEnum, UpsertApplicantV2Dto } from '@inosio/payments-sdk';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ResetPasswordIcon from '../../../icons/ResetPasswordIcon.svg';
import { useCallback, useEffect, useState } from 'react';
import { getApplicationV2, inviteApplicantV2, selectApplicantsState } from '../sagas/applicants/applicantsSlice';
import { EmailRegex } from '../../../utils/regex';

const useStyles = makeStyles({
  modalContainer: {
    background: '#FFFFFF',
    boxShadow: 'none',
    borderRadius: '24px',
  },
  cardSytle: {
    position: 'relative' as 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '22px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    minHeight: '523px',
    borderRadius: '24px',
    outline: 'none',
  },
  titleText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    textAlign: 'center',
  },
  signUpBtn: {
    color: '#fff !important',
    background: 'rgba(57, 162, 219, 1) !important',
    borderRadius: '24px !important',
    padding: '9px 17px !important',
    height: '49px',
    '&.Mui-disabled': {
      background: 'rgba(211, 211, 211, 1) !important',
    },
  },
  signUpBtnText: {
    textTransform: 'capitalize',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    fontWeight: '700 !important',
    fontFamily: 'Work Sans !important',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '100%',
    height: '49px',
    marginTop: '24px',
  },
  StatusSelect: {
    textAlign: 'center',
    borderRadius: '24px !important',
    width: '100%',
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '14px !important',
    color: '#000000 !important',
    boxShadow: '0px 0px 40px 0px rgba(0, 0, 0, 0.1) !important',
    height: '49px  !important',
  },
});

const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '24px',
    },
  },
};

export enum AddCoApplicantsModalStates {
  invite = 'invite',
  view = 'view',
  success = 'success',
}

interface AddCoApplicantsModalProps {
  open: boolean;
  applicationUuid: string;
  closeCallback: () => void;
}

//Applicant type options
//important! the values should match the entries in the db
const applicantTypes = [
  { key: ApplicantTypeEnum.Occupant, value: 'Occupant' },
  { key: ApplicantTypeEnum.Cosigner, value: 'Cosigner/ Guarantor' },
];

export default function AddCoApplicantsModal(props: AddCoApplicantsModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { InvitedApplicant, isInvitingApplicant, isInvitingApplicantSuccess, isInvitingApplicantFailed } = useSelector(selectApplicantsState);

  const [modalState, setModalState] = useState(AddCoApplicantsModalStates.invite);
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState('');
  const [applicantType, setApplicantType] = useState('');
  const [isErrorApplicantType, setIsErrorApplicantType] = useState(false);

  const handleSendInvite = () => {
    setGlobalError('');

    if (!firstName) {
      setFirstNameError('FirstName is required.');
    } else {
      setFirstNameError('');
    }
    if (!lastName) {
      setLastNameError('LastName is required.');
    } else {
      setLastNameError('');
    }
    if (!email) {
      setEmailError('Email is required.');
    }
    if (!EmailRegex.test(email)) {
      setEmailError('Please enter a valid email');
    }
    if (!applicantType) {
      setIsErrorApplicantType(true);
    } else {
      setEmailError('');
    }

    if (firstName && lastName && email && applicantType) {
      dispatch({
        type: inviteApplicantV2.type,
        payload: {
          applicantDto: {
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            ApplicantType: applicantType as ApplicantTypeEnum,
          } as UpsertApplicantV2Dto,
          inviteToApplicationUuid: props.applicationUuid,
        },
      });
    }
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  const applicantTypeChangeHandler = (e: any) => {
    setApplicantType(e.target.value);
    setIsErrorApplicantType(false);
  };

  useEffect(() => {
    if (isInvitingApplicant) {
      setIsSpinnerModalOpen(true);
    } else if (isInvitingApplicantSuccess) {
      setSpinnerStatus('stop');
      setModalState(AddCoApplicantsModalStates.success);
      setInvitationLink(InvitedApplicant?.InvitationLink!);
      //re-fetch the application so the UI updates with the new applicant's info
      dispatch({
        type: getApplicationV2.type,
        payload: props.applicationUuid,
      });
    } else if (isInvitingApplicantFailed) {
      setIsSpinnerModalOpen(false);
      setGlobalError('Something went wrong, please try again later.');
    }
  }, [isInvitingApplicant, isInvitingApplicantSuccess, isInvitingApplicantFailed, InvitedApplicant]);

  return (
    <div className={`${classes.modalContainer}`}>
      <Modal
        open={props.open}
        onClose={() => props.closeCallback()}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Card className={classes.cardSytle}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <Typography className={classes.titleText} sx={{ mt: 1 }}>
              {modalState === AddCoApplicantsModalStates.invite
                ? 'Add Adult Occupant to Application'
                : modalState === AddCoApplicantsModalStates.view
                ? 'Add Additional Adult Occupants'
                : 'Co-Applicant Invited'}
            </Typography>
            <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
              <IconButton sx={{ mr: '-6px' }} onClick={() => props.closeCallback()}>
                <Icon>
                  <img src={CancelIcon} alt="" width={24} height={24} />
                </Icon>
              </IconButton>
            </Box>
          </Box>

          {globalError?.length ? (
            <div className={classes.globalError}>
              <Typography
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
              >
                {globalError}
              </Typography>
            </div>
          ) : (
            <></>
          )}

          <Box>
            {modalState === AddCoApplicantsModalStates.invite ? (
              <Box sx={{ textAlign: 'center' }}>
                <img src={InviteResidentIcon} style={{ maxWidth: 300, marginBottom: '10px' }} alt="" />
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} mb={1}>
                    <FormControl fullWidth error={isErrorApplicantType}>
                      <Select
                        onChange={applicantTypeChangeHandler}
                        value={applicantType || ''}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? <Typography style={{ fontSize: '16px' }}>{selected}</Typography> : <Typography style={{ color: 'gray', fontSize: '16px' }}>Select Type of Co-Applicant</Typography>
                        }
                        inputProps={{ 'aria-label': 'Without label' }}
                        className={classes.StatusSelect}
                        MenuProps={MenuProps}
                      >
                        {applicantTypes.map((option) => (
                          <MenuItem value={option.key} key={option.key}>
                            {option.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {isErrorApplicantType && <FormHelperText sx={{ mt: 1 }}>Please select a type of co-applicant</FormHelperText>}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ mb: -1 }}>
                    <InputField
                      name={'first_name'}
                      placeholder={'First Name'}
                      label={'First Name'}
                      isError={false}
                      errors={firstNameError}
                      touched={true}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ mb: -1 }}>
                    <InputField name={'last_name'} placeholder={'Last Name'} label={'Last Name'} isError={false} errors={lastNameError} touched={true} onChange={(e) => setLastName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <InputField name={'email'} placeholder={'Email'} label={'Email'} isError={false} errors={emailError} touched={true} onChange={(e) => setEmail(e.target.value)} />
                  </Grid>
                </Grid>
                <div className={classes.wrapper} style={{ textAlign: 'right', marginTop: '30px' }}>
                  <Button onClick={handleSendInvite} className={classes.signUpBtn} sx={{ height: 33 }}>
                    <Typography className={classes.signUpBtnText} sx={{ fontWeight: '400 !important' }}>
                      Send Invite
                    </Typography>
                  </Button>
                </div>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <img src={ResetPasswordIcon} style={{ maxWidth: 120, marginTop: '20px' }} alt="" />

                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: '25px',
                    color: '#14142B',
                    textAlign: 'center',
                    marginTop: '22px !important',
                    px: 2,
                  }}
                >
                  Your co-applicant has been sent an email invitation to join your application
                </Typography>

                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: '25px',
                    color: '#14142B',
                    textAlign: 'center',
                    marginTop: '22px !important',
                    px: 2,
                  }}
                >
                  Your application cannot be submitted until ALL applicants have completed their data.
                </Typography>

                <Box
                  sx={{
                    background: '#F1F0F5',
                    borderRadius: '24px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    marginTop: '25px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#14142B',
                      width: 'calc( 100% - 80px )',
                    }}
                  >
                    Click here to copy the invitation link and share with them
                  </Typography>
                  <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="Copy special code"
                    onClick={() => {
                      navigator.clipboard.writeText(invitationLink);
                      setSnackBarOpen(true);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 5,
                    }}
                  >
                    <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      </Modal>

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          isInvisible={true}
          fastSpinner={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {/* {isDeleteModalOpen && <DeleteModal open={isDeleteModalOpen} isInvisible={true} confirmCallback={handleConfirmDeleteModal} closeCallback={() => setIsDeleteModalOpen(false)} />} */}
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarOpen(false);
        }}
      >
        <Alert
          onClose={() => {
            setSnackBarOpen(false);
          }}
          severity="success"
          sx={{ width: '100%' }}
        >
          Copied to Clipboard
        </Alert>
      </Snackbar>
    </div>
  );
}
